import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError, rupiah } from "@/lib/api";
import { Empty, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ArrowLeft, Wallet, PiggyBank, Sparkles, Loader2, Plus, ArrowDownToLine, CreditCard,
} from "lucide-react";
import { toast } from "sonner";
import { PaymentMethodDialog } from "@/components/PaymentMethodDialog";

export default function StudentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [bills, setBills] = useState([]);
  const [tx, setTx] = useState([]);
  const [summary, setSummary] = useState("");
  const [sumLoading, setSumLoading] = useState(false);
  const [pay, setPay] = useState(null); // {type, bill}
  const [amount, setAmount] = useState("");
  const [processing, setProcessing] = useState(false);
  const [methodOpen, setMethodOpen] = useState(false);
  const [pendingCheckout, setPendingCheckout] = useState(null); // {purpose, bill_id, amount}

  const load = useCallback(() => {
    api.get("/parent/students").then((r) => setStudent(r.data.find((s) => s.id === id)));
    api.get(`/parent/students/${id}/bills`).then((r) => setBills(r.data)).catch(() => {});
    api.get(`/parent/students/${id}/transactions`).then((r) => setTx(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const openPay = (type, bill = null) => {
    setPay({ type, bill });
    if (type === "bill" && bill) setAmount(String(bill.total_amount - bill.paid_amount));
    else setAmount("");
  };

  const submitPayment = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Masukkan jumlah yang valid");
    if (pay.type === "withdraw") {
      setProcessing(true);
      try {
        await api.post("/parent/withdrawals", { student_id: id, amount: amt, note: "Penarikan via aplikasi" });
        toast.success("Permintaan penarikan dikirim ke admin");
        setPay(null);
        load();
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
      } finally {
        setProcessing(false);
      }
      return;
    }
    const purpose = pay.type === "bill" ? "bill_payment" : pay.type === "topup" ? "personal_topup" : "savings_deposit";
    setPendingCheckout({ purpose, bill_id: pay.bill?.id || null, amount: amt });
    setPay(null);
    setMethodOpen(true);
  };

  const confirmMethod = async (method, channel) => {
    setProcessing(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        ...pendingCheckout,
        student_id: id,
        origin_url: window.location.origin,
        payment_method: method,
        payment_channel: channel,
      });
      setMethodOpen(false);
      if (data.redirect_url) window.location.href = data.redirect_url;
      else navigate(`/payment/instructions?session_id=${data.session_id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setProcessing(false);
    }
  };

  const genSummary = async () => {
    setSumLoading(true);
    try {
      const { data } = await api.post("/ai/bill-summary", { student_id: id });
      setSummary(data.summary);
    } catch (e) {
      toast.error("Gagal memuat ringkasan AI");
    } finally {
      setSumLoading(false);
    }
  };

  if (!student) return <p className="text-stone-400">Memuat…</p>;

  const dialogTitle = {
    bill: `Bayar ${pay?.bill?.label || ""}`,
    topup: "Top-up Uang Jajan",
    savings: "Setor Tabungan",
    withdraw: "Tarik Tabungan",
  };

  return (
    <div>
      <Link to="/parent" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-emerald-600 mb-5" data-testid="back-link">
        <ArrowLeft size={16} /> Kembali
      </Link>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center font-heading font-bold text-white text-xl ${student.gender === "putra" ? "bg-blue-500" : "bg-pink-500"}`}>
            {student.name.charAt(0)}
          </div>
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-stone-900">{student.name}</h1>
            <p className="text-stone-400 font-mono text-sm">NISN {student.nisn} · {student.gender}</p>
          </div>
        </div>
      </div>

      {/* Balances */}
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <div className="bg-gradient-to-br from-emerald-600 to-emerald-800 rounded-2xl p-5 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <p className="text-emerald-100 text-sm flex items-center gap-2"><Wallet size={16} /> Uang Jajan</p>
          </div>
          <p className="font-heading text-3xl font-bold mt-2">{rupiah(student.personal_balance)}</p>
          <Button size="sm" onClick={() => navigate("/parent/bayar", { state: { focus: "topup", studentId: id } })} data-testid="topup-button" className="mt-3 bg-white/15 hover:bg-white/25 rounded-lg">
            <Plus size={15} className="mr-1" /> Top-up
          </Button>
        </div>
        <div className="bg-white border border-stone-200 rounded-2xl p-5 shadow-sm">
          <p className="text-stone-500 text-sm flex items-center gap-2"><PiggyBank size={16} className="text-amber-500" /> Tabungan Santri</p>
          <p className="font-heading text-3xl font-bold text-stone-900 mt-2">{rupiah(student.savings_balance)}</p>
          <div className="mt-3 flex gap-2">
            <Button size="sm" onClick={() => navigate("/parent/bayar", { state: { focus: "savings", studentId: id } })} data-testid="savings-deposit-button" className="bg-emerald-600 hover:bg-emerald-700 rounded-lg">
              <Plus size={15} className="mr-1" /> Setor
            </Button>
            <Button size="sm" variant="outline" onClick={() => openPay("withdraw")} data-testid="savings-withdraw-button" className="rounded-lg">
              <ArrowDownToLine size={15} className="mr-1" /> Tarik
            </Button>
          </div>
        </div>
      </div>

      {/* AI summary */}
      <div className="bg-stone-900 rounded-2xl p-5 mb-6 text-white">
        <div className="flex items-center justify-between mb-2">
          <p className="font-heading font-semibold flex items-center gap-2"><Sparkles size={16} className="text-emerald-400" /> Ringkasan AI</p>
          <Button size="sm" onClick={genSummary} disabled={sumLoading} data-testid="ai-summary-button" className="bg-emerald-600 hover:bg-emerald-700 rounded-lg">
            {sumLoading ? <Loader2 size={15} className="animate-spin" /> : "Ringkas"}
          </Button>
        </div>
        <p className="text-stone-300 text-sm leading-relaxed whitespace-pre-line" data-testid="ai-summary-text">
          {summary || "Klik \"Ringkas\" untuk ringkasan status pembayaran anak Anda oleh AI."}
        </p>
      </div>

      <Tabs defaultValue="bills">
        <TabsList>
          <TabsTrigger value="bills" data-testid="tab-bills">Tagihan</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Riwayat Uang Jajan</TabsTrigger>
        </TabsList>

        <TabsContent value="bills" className="mt-4 space-y-3">
          {bills.length === 0 ? (
            <Empty>Belum ada tagihan.</Empty>
          ) : (
            bills.map((b) => {
              const remaining = b.total_amount - b.paid_amount;
              return (
                <div key={b.id} data-testid={`parent-bill-${b.id}`} className="bg-white border border-stone-200 rounded-2xl p-4 shadow-sm flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-stone-800">{b.label}</p>
                      <StatusBadge status={b.status} />
                    </div>
                    <p className="text-xs text-stone-400 mt-1">
                      Jatuh tempo {b.due_date} · Total {rupiah(b.total_amount)}
                      {b.paid_amount > 0 && ` · Dibayar ${rupiah(b.paid_amount)}`}
                      {b.installment_allowed && " · bisa dicicil"}
                    </p>
                  </div>
                  {b.status !== "paid" && (
                    <Button size="sm" onClick={() => navigate("/parent/bayar", { state: { preselect: b.id } })} data-testid={`pay-bill-${b.id}`} className="bg-emerald-600 hover:bg-emerald-700 rounded-lg">
                      <CreditCard size={15} className="mr-1.5" /> Bayar {rupiah(remaining)}
                    </Button>
                  )}
                </div>
              );
            })
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          {tx.length === 0 ? (
            <Empty>Belum ada transaksi uang jajan.</Empty>
          ) : (
            <div className="bg-white border border-stone-200 rounded-2xl shadow-sm divide-y">
              {tx.map((t) => (
                <div key={t.id} className="flex items-center justify-between p-4">
                  <div>
                    <p className="text-sm font-medium text-stone-800">{t.note}</p>
                    <p className="text-xs text-stone-400">{new Date(t.created_at).toLocaleString("id-ID")}</p>
                  </div>
                  <span className={`font-mono font-semibold ${t.amount >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                    {t.amount >= 0 ? "+" : ""}{rupiah(t.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Payment Dialog */}
      <Dialog open={!!pay} onOpenChange={(o) => !o && setPay(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{pay && dialogTitle[pay.type]}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            {pay?.type === "bill" && pay.bill && (
              <div className="rounded-xl bg-stone-50 border p-3 text-sm text-stone-600">
                Sisa tagihan: <b>{rupiah(pay.bill.total_amount - pay.bill.paid_amount)}</b>
                {pay.bill.installment_allowed && <p className="text-xs text-amber-600 mt-1">Tagihan ini bisa dicicil — masukkan nominal cicilan.</p>}
              </div>
            )}
            <div className="space-y-2">
              <Label>Jumlah (Rp)</Label>
              <Input type="number" data-testid="payment-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-xl" placeholder="0" />
            </div>
            {pay?.type !== "withdraw" && (
              <p className="text-xs text-stone-400">Selanjutnya Anda akan memilih metode pembayaran (Virtual Account / QRIS / e-wallet).</p>
            )}
          </div>
          <DialogFooter>
            <Button onClick={submitPayment} disabled={processing} data-testid="confirm-payment-button" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">
              {processing ? <Loader2 className="animate-spin" /> : pay?.type === "withdraw" ? "Kirim Permintaan" : "Lanjut Bayar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PaymentMethodDialog
        open={methodOpen}
        onOpenChange={setMethodOpen}
        total={pendingCheckout?.amount || 0}
        confirming={processing}
        onConfirm={confirmMethod}
      />
    </div>
  );
}
